import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, CheckCircle, XCircle, ArrowLeft, Save, AlertTriangle, FileText, Truck, Building2, Calendar } from 'lucide-react';
import { useToast, LoadingState } from '../components/ui';
import api from '../services/api';

const MaterialReceipt = () => {
  const { operationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [materialReceipts, setMaterialReceipts] = useState({});
  const [notes, setNotes] = useState('');

  // Fetch PO materials for this unloading operation
  const { data: poData, isLoading } = useQuery({
    queryKey: ['po-materials', operationId],
    queryFn: async () => {
      const response = await api.get(`/warehouse/unloading-operations/${operationId}/po_materials/`);
      return response.data;
    },
    enabled: !!operationId
  });

  // Initialize material receipts with default values (auto-populate from Form 103 if available)
  useEffect(() => {
    if (poData?.materials) {
      const initialReceipts = {};
      poData.materials.forEach(material => {
        initialReceipts[material.po_line_id] = {
          po_line_id: material.po_line_id,
          // Auto-populate from Form 103 if available, otherwise use pending quantity
          received_quantity: material.has_form_103_data
            ? material.received_now
            : material.pending_quantity,
          batch_number: material.batch_number || '',
          notes: material.notes || '',
          dock_id: material.recommended_dock_id || poData.primary_dock?.id  // Auto-select recommended dock
        };
      });
      setMaterialReceipts(initialReceipts);
    }
  }, [poData]);

  // Start and complete unloading mutation
  const completeReceiptMutation = useMutation({
    mutationFn: async (data) => {
      // First, start the unloading operation
      await api.post(`/warehouse/unloading-operations/${operationId}/start/`);

      // Then complete it with material data
      const response = await api.post(`/warehouse/unloading-operations/${operationId}/complete/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      success('Material receipt completed successfully!');
      navigate('/dock-management');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to complete material receipt');
    }
  });

  const handleMaterialChange = (poLineId, field, value) => {
    setMaterialReceipts(prev => ({
      ...prev,
      [poLineId]: {
        ...prev[poLineId],
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert materialReceipts object to array
    const materials = Object.values(materialReceipts).filter(
      receipt => parseFloat(receipt.received_quantity) > 0
    );

    if (materials.length === 0) {
      showError('Please enter received quantity for at least one material');
      return;
    }

    completeReceiptMutation.mutate({ materials });
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All entered data will be lost.')) {
      navigate('/dock-management');
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading PO materials..." />;
  }

  if (!poData) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load PO materials</p>
        </div>
      </div>
    );
  }

  const po = poData.purchase_order;
  const materials = poData.materials || [];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dock-management')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dock Management
        </button>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Package className="w-8 h-8 mr-3 text-blue-600" />
              Material Receipt
            </h1>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              Unloading in Progress
            </span>
          </div>

          {/* PO Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">PO Number</p>
                <p className="font-semibold text-gray-900">{po?.po_number || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Supplier</p>
                <p className="font-semibold text-gray-900">{po?.supplier_name || 'N/A'}</p>
                <p className="text-xs text-gray-500">{po?.supplier_code}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Expected Delivery</p>
                <p className="font-semibold text-gray-900">
                  {po?.expected_delivery_date ? new Date(po.expected_delivery_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center space-x-4 text-sm">
            <div>
              <span className="text-gray-500">PO Date:</span>
              <span className="ml-2 font-medium">{po?.po_date ? new Date(po.po_date).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">Total Value:</span>
              <span className="ml-2 font-medium">{po?.currency} {po?.total_value?.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-gray-500">Total Lines:</span>
              <span className="ml-2 font-medium">{materials.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form 103 Status Banner */}
      {poData.has_form_103 && (
        <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Form 103 (GRN) Data Auto-Populated
              </p>
              <p className="text-xs text-green-700 mt-1">
                GRN #{poData.form_103_grn_number} - Invoice quantities, batch numbers, and dates have been automatically filled from Form 103. Please verify and adjust if needed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Material Receipt Form */}
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Material Receipt Details</h2>
            <p className="text-sm text-gray-600 mt-1">
              {poData.has_form_103
                ? 'Verify the auto-populated quantities and batch numbers from Form 103. Select unloading dock for each material (recommended docks are pre-selected based on material category). Adjust if needed.'
                : 'Enter the actual quantities received for each material. Select unloading dock for each material (different materials can be unloaded at different docks). Verify batch numbers and inspect quality.'}
            </p>
          </div>

          {/* Materials Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Line #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ordered
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Already Received
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pending
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Received Now *
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unloading Dock *
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Batch Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {materials.map((material) => {
                  const receipt = materialReceipts[material.po_line_id] || {};
                  const receivedQty = parseFloat(receipt.received_quantity || 0);
                  const pendingQty = material.pending_quantity;
                  const isOverDelivery = receivedQty > pendingQty;
                  const isUnderDelivery = receivedQty < pendingQty && receivedQty > 0;

                  return (
                    <tr key={material.po_line_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {material.line_number}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{material.material_code}</p>
                        <p className="text-xs text-gray-500">{material.category}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{material.material_description}</p>
                        {material.qc_required && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                            QC Required
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-900">
                        {material.ordered_quantity} {material.unit_of_measure}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-900">
                        {material.already_received} {material.unit_of_measure}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">
                        {material.pending_quantity} {material.unit_of_measure}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={receipt.received_quantity || ''}
                            onChange={(e) => handleMaterialChange(material.po_line_id, 'received_quantity', e.target.value)}
                            className={`w-24 px-2 py-1.5 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              isOverDelivery ? 'border-orange-300 bg-orange-50' :
                              isUnderDelivery ? 'border-yellow-300 bg-yellow-50' :
                              material.has_form_103_data ? 'border-green-300 bg-green-50' :
                              'border-gray-300'
                            }`}
                            required
                          />
                          <span className="text-xs text-gray-500">{material.unit_of_measure}</span>
                          {isOverDelivery && (
                            <AlertTriangle className="w-4 h-4 text-orange-500" title="Over delivery!" />
                          )}
                        </div>
                        {isOverDelivery && (
                          <p className="text-xs text-orange-600 mt-1">Over by {(receivedQty - pendingQty).toFixed(2)}</p>
                        )}
                        {material.has_form_103_data && (
                          <p className="text-xs text-green-600 mt-1 flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            From Form 103
                          </p>
                        )}
                        {isUnderDelivery && (
                          <p className="text-xs text-yellow-600 mt-1">Short by {(pendingQty - receivedQty).toFixed(2)}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={receipt.dock_id || ''}
                          onChange={(e) => handleMaterialChange(material.po_line_id, 'dock_id', e.target.value)}
                          className={`w-36 px-2 py-1.5 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            material.recommended_dock_id && receipt.dock_id === material.recommended_dock_id
                              ? 'border-green-300 bg-green-50'
                              : 'border-gray-300'
                          }`}
                          required
                        >
                          <option value="">Select Dock</option>
                          {poData?.available_docks?.map(dock => (
                            <option key={dock.id} value={dock.id}>
                              {dock.dock_number} - {dock.location}
                              {dock.material_type === material.category ? ' ✓' : ''}
                            </option>
                          ))}
                        </select>
                        {material.recommended_dock_id && receipt.dock_id === material.recommended_dock_id && (
                          <p className="text-xs text-green-600 mt-1 flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Recommended for {material.category}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={receipt.batch_number || ''}
                          onChange={(e) => handleMaterialChange(material.po_line_id, 'batch_number', e.target.value)}
                          placeholder="Batch #"
                          className="w-28 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={receipt.notes || ''}
                          onChange={(e) => handleMaterialChange(material.po_line_id, 'notes', e.target.value)}
                          placeholder="Notes..."
                          className="w-32 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Overall Notes */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Overall Receipt Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any general notes about this receipt..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex items-center justify-between bg-white rounded-lg shadow-sm p-6">
          <div className="text-sm text-gray-600">
            <AlertTriangle className="w-4 h-4 inline mr-2 text-yellow-500" />
            Review all quantities carefully before submitting. This action cannot be undone.
          </div>
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              disabled={completeReceiptMutation.isLoading}
            >
              <XCircle className="w-4 h-4 inline mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={completeReceiptMutation.isLoading}
            >
              {completeReceiptMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Completing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete Receipt & Start QC
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MaterialReceipt;
